/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { trackCustomEvent } from '@medflex/utils/Tracking';
import Link from '../Link';
import { triggerToast, EVENTS } from '../Toast';
import CopyButton from '../CopyButton';
import { IconEnvelope, IconPhone, IconStarFull, IconUser } from '../Icons';

const ContactDetails = ({
  representativeElement,
  fullNameWithTitle = '',
  dateOfBirth = '',
  email = '',
  phoneNumber = '',
  isContactPaired = false,
  className = '',
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'PamInquiry.contactDetails' });

  const details = useMemo(
    () => [
      ...(fullNameWithTitle
        ? [
            {
              name: 'fullNameWithTitle',
              Icon: IconUser,
              className: representativeElement ? 'items-baseline' : null,
              field: fullNameWithTitle ? (
                <div className="flex flex-col space-y-1">
                  <span>{fullNameWithTitle}</span>
                  {representativeElement && representativeElement}
                </div>
              ) : (
                t('notSpecified')
              ),
            },
          ]
        : []),
      ...(dateOfBirth
        ? [
            {
              name: 'dateOfBirth',
              Icon: IconStarFull,
              field: dateOfBirth ? format(dateOfBirth, 'DD.MM.YYYY') : t('notSpecified'),
              className: 'items-center',
            },
          ]
        : []),
      ...(email
        ? [
            {
              name: 'email',
              Icon: IconEnvelope,
              field: email || t('notSpecified'),
              canCopy: !!email,
              successText: t('emailCopied'),
              className: 'items-center',
            },
          ]
        : []),
      ...(!phoneNumber && !email
        ? [
            {
              name: 'unkownCaller',
              Icon: IconPhone,
              field: t('unknown'),
              className: 'items-center',
            },
          ]
        : []),
      ...(phoneNumber
        ? [
            {
              name: 'phoneNumber',
              Icon: IconPhone,
              field: phoneNumber,
              canCopy: !!phoneNumber,
              successText: t('phoneNumberCopied'),
              className: 'items-center',
            },
          ]
        : []),
    ],
    [fullNameWithTitle, representativeElement, t, dateOfBirth, email, phoneNumber],
  );

  const onCopyClick = successText => {
    triggerToast(EVENTS.SUCCESS, {
      title: successText,
      options: {
        toastId: 'contactDetailsCopied',
      },
    });

    trackCustomEvent({
      eventCategory: 'physician_inbox',
      eventAction: 'ContactDetails | button | email_copy',
      eventValue: isContactPaired ? 1 : 0,
    });
  };

  const handleEmailTracking = () => {
    trackCustomEvent({
      eventCategory: 'user interactions',
      eventAction: 'ContactDetails | link | mailto',
      eventValue: 'inbox',
    });
  };

  const handleTelTracking = () => {
    trackCustomEvent({
      eventCategory: 'user interactions',
      eventAction: 'ContactDetails | link | tel',
      eventValue: 'inbox',
    });
  };

  const renderDetailLinks = (name, field) => {
    switch (name) {
      case 'email':
        return (
          <Link rel="noreferrer" onClick={handleEmailTracking} href={`mailto:${field}`}>
            <span className="shrink truncate">{field}</span>
          </Link>
        );
      case 'phoneNumber':
        return (
          field && (
            <Link rel="noreferrer" onClick={handleTelTracking} href={`tel:${field}`}>
              <span className="shrink truncate">{field}</span>
            </Link>
          )
        );
      default:
        return <span className="shrink truncate">{field}</span>;
    }
  };

  return (
    <div data-component="ContactDetails" className={className}>
      <h3 className="text-lg font-semibold mb-4">{t('contactInformation')}</h3>
      <div className="space-y-4">
        {details.map(({ Icon, field, canCopy, name, className, successText }) => (
          <div key={name} className={`flex space-x-3 ${className}`}>
            <Icon size="4" className="shrink-0 text-slate-600" />
            {renderDetailLinks(name, field)}
            {canCopy && navigator?.clipboard && (
              <CopyButton value={field} onClick={() => onCopyClick(successText)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

ContactDetails.propTypes = {
  isContactPaired: PropTypes.bool,
  fullNameWithTitle: PropTypes.string,
  dateOfBirth: PropTypes.string,
  email: PropTypes.string,
  phoneNumber: PropTypes.string,
  representativeElement: PropTypes.element,
  className: PropTypes.string,
};

export default ContactDetails;
